export type CertifiedRegime = "REGIME 1" | "REGIME 2" | "REGIME 3";
export type CertifiedMarginKey = "C1" | "C2" | "C3" | "C4";

export interface CertifiedMargin {
  readonly name: string;
  readonly value: number;
  readonly threshold: number;
  readonly note?: string;
}

export interface DriftGradient {
  readonly axis: string;
  readonly drift: number;
}

export interface CertifiedManifoldState {
  readonly certificationMargin: number;
  readonly regime: CertifiedRegime;
  readonly worstDrift: DriftGradient;
}

export const CERTIFIED_MARGINS: Readonly<Record<CertifiedMarginKey, CertifiedMargin>> = {
  C1: { name: "Authority (PERMIT)", value: 0.95, threshold: 0.1 },
  C2: { name: "Knowledge (SEPARATE)", value: 0.88, threshold: 0.1 },
  C3: { name: "Temporal (PRECOMMIT)", value: 0.85, threshold: 0.1 },
  C4: { name: "Resource (RES)", value: 1.0, threshold: 0, note: "automatic" },
};

export const BOP_DRIFT_GRADIENT: readonly DriftGradient[] = [
  { axis: "G1 (Authority)", drift: -0.002 },
  { axis: "G2 (Knowledge)", drift: -0.005 },
  { axis: "G3 (Temporal)", drift: -0.001 },
  { axis: "G4 (Resource)", drift: 0.0 },
];

export function certifiedRegime(certificationMargin: number): CertifiedRegime {
  if (certificationMargin > 0.5) return "REGIME 1";
  return certificationMargin > 0 ? "REGIME 2" : "REGIME 3";
}

export function summarizeCertifiedManifold(
  margins: Readonly<Record<CertifiedMarginKey, CertifiedMargin>>,
  gradient: readonly DriftGradient[],
): CertifiedManifoldState {
  const certificationMargin = Math.min(
    ...Object.values(margins).map((margin) => margin.value),
  );
  return {
    certificationMargin,
    regime: certifiedRegime(certificationMargin),
    worstDrift: gradient.reduce((worst, entry) =>
      worst.drift < entry.drift ? worst : entry,
    ),
  };
}

export function driftTone(drift: number): "success" | "warning" | "danger" {
  if (drift < -0.003) return "danger";
  if (drift < 0) return "warning";
  return "success";
}
